// File: frontend/src/layouts/MainContent.tsx
// src/layouts/MainContent.tsx
import React, { useRef } from "react";
import "./MainContent.css";
import { EntryModel, MatchModel } from "../models";
import { Pagination, SpinnerLoading } from "../utils";
import { EntryList, MatchTitleOfEntries, EntryPostForm, EntryToolbar, CollapsedEntriesBar } from "../components/entries";
// Sitewide pins: admin-pinned entry shown at the top of the match page
import { PinnedEntryBlock } from "../components/entries/PinnedEntryBlock";
import { EntrySortOption } from "../components/entries/EntryToolbar";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { FaArrowLeft } from "react-icons/fa";
import { generateMatchUrlFromEntry } from "../utils/slugGenerator";
import { buildTopicShareUrl } from "../utils/shareUtils";

interface MainContentProps {
  entries: EntryModel[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  // Page size from pagination state — used to compute remaining entries correctly
  // regardless of whether the user appended pages (load-more) or navigated directly (pagination).
  pageSize: number;
  paginate: (page: number) => void;
  selectedMatch: MatchModel | null;
  onPostEntry: (content: string) => Promise<void>;
  // Mobile-only: callback to go back to the match list
  onMobileBack?: () => void;
  // Issue B: called by EntryRow after soft-delete so App.tsx can filter the entry out
  onDeleteEntry?: (entryId: number) => void;
  // Toolbar state — managed in App.tsx so sort/filter survive pagination
  sort: EntrySortOption;
  onSortChange: (sort: EntrySortOption) => void;
  filterToday: boolean;
  onFilterTodayChange: (value: boolean) => void;
  rookieOnly: boolean;
  onRookieOnlyChange: (value: boolean) => void;
  keyword: string;
  onKeywordChange: (value: string) => void;
  // Load-more append: fetches the next page and appends to the list
  loadMoreEntries?: () => Promise<void>;
  isLoadingMoreEntries?: boolean;
  // When active, `entries` contains only today's entries (+ any revealed older
  // chunks) and pagination / bottom load-more are hidden.
  collapseActive?: boolean;
  // True while the collapse detection requests are running (show spinner)
  collapseChecking?: boolean;
  // Permalink ("?tanim=747") view: a single shared tanım framed by a
  // "{n} entry daha" bar on each side, instead of the whole page it sits on.
  focusActive?: boolean;
  focusLoading?: boolean;
  focusHiddenAbove?: number;
  focusHiddenBelow?: number;
  onExitFocus?: () => void;
  // How many older entries are still hidden behind the top bar
  collapseHiddenCount?: number;
  // Reveals the previous chunk of older entries
  onLoadOlderEntries?: () => void;
  isLoadingOlderEntries?: boolean;
}

export const MainContent: React.FC<MainContentProps> = ({
  entries,
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  paginate,
  selectedMatch,
  onPostEntry,
  onMobileBack,
  onDeleteEntry,
  sort,
  onSortChange,
  filterToday,
  onFilterTodayChange,
  rookieOnly,
  onRookieOnlyChange,
  keyword,
  onKeywordChange,
  loadMoreEntries,
  isLoadingMoreEntries = false,
  collapseActive = false,
  collapseChecking = false,
  focusActive = false,
  focusLoading = false,
  focusHiddenAbove = 0,
  focusHiddenBelow = 0,
  onExitFocus,
  collapseHiddenCount = 0,
  onLoadOlderEntries,
  isLoadingOlderEntries = false,
}) => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  // Ref used by the toolbar "sayfa sonu" button to scroll this element to the bottom
  const mainRef = useRef<HTMLElement>(null);

  const handleScrollToBottom = () => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: mainRef.current.scrollHeight, behavior: "smooth" });
    }
  };

  // Determine if a match is selected
  const isMatchSelected = selectedMatch !== null;
  // Determine if there are no entries for the selected match
  // (skipped while the collapse detection is still running)
  const hasNoEntries = isMatchSelected && entries.length === 0 && !collapseChecking && !focusLoading;

  // Remaining entries = entries that exist on pages AFTER the current one.
  // Using (currentPage * pageSize) instead of entries.length because in replace mode
  // (direct pagination navigation) entries.length is always one page's worth, which
  // gives the wrong remaining count when the user is on page > 1.
  // In append mode (load-more) the two formulas are equivalent since
  // entries.length == currentPage * pageSize when pages are full.
  const remainingEntries = Math.max(0, totalItems - currentPage * pageSize);

  // Show "load more" button when there are more pages and remaining entries to display.
  // Hidden in collapsed mode — all of today's entries are already loaded there.
  const hasMoreEntries = !collapseActive && !focusActive && currentPage < totalPages && remainingEntries > 0 && !!loadMoreEntries;

  return (
    <main className="main-content" ref={mainRef}>
      {/* Mobile back button — only rendered on small screens via CSS */}
      {onMobileBack && (
        <button className="mobile-back-btn" onClick={onMobileBack} aria-label="Geri">
          <FaArrowLeft size={14} />
          <span>{t("mobile_back", "Geri")}</span>
        </button>
      )}

      {/* Always show the match title component, it can handle null match internally */}
      <MatchTitleOfEntries match={selectedMatch} />

      {/* Conditionally render content based on whether a match is selected */}
      {isMatchSelected ? (
        <>
          {/* Toolbar: search, filters, sort, scroll-to-bottom — always visible when a match is selected */}
          <EntryToolbar
            sort={sort}
            onSortChange={onSortChange}
            filterToday={filterToday}
            onFilterTodayChange={onFilterTodayChange}
            rookieOnly={rookieOnly}
            onRookieOnlyChange={onRookieOnlyChange}
            keyword={keyword}
            onKeywordChange={onKeywordChange}
            onScrollToBottom={handleScrollToBottom}
            /* Topic-level share: carries the page the reader is on, so
               "share" from page 4 hands out page 4. buildTopicShareUrl reads
               "?sayfa=" off the current address — passing the bare path here
               is what made every shared link open at page 1. */
            shareUrl={selectedMatch ? buildTopicShareUrl(generateMatchUrlFromEntry(
              selectedMatch.homeTeam,
              selectedMatch.awayTeam,
              new Date(selectedMatch.matchDate),
              selectedMatch.id
            )) : null}
            shareTitle={selectedMatch ? `${selectedMatch.homeTeam} - ${selectedMatch.awayTeam}` : ""}
          />

          {/* Show "no entries" message if there are no entries for the selected match */}
          {hasNoEntries && (
            <p className="no-entries-message">{t("no_entries_message")}</p>
          )}

          {/* Spinner while the collapse detection requests are running */}
          {(collapseChecking || focusLoading) && <SpinnerLoading />}

          {/* Render entries + navigation only if there are entries */}
          {!collapseChecking && !focusLoading && entries.length > 0 && (
            <>
              {/* Pagination is hidden in collapsed mode — the top bar replaces it */}
              {!collapseActive && !focusActive && (
                <div className="pagination-top">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    paginate={paginate}
                  />
                </div>
              )}

              {/* Permalink view: the shared tanım is framed by a "{n} entry daha"
                  bar on each side. Clicking either one leaves the focused view
                  for the full page this entry sits on, with the entry still on
                  screen — the bars are the way back into the topic. */}
              {focusActive && onExitFocus && (
                <CollapsedEntriesBar
                  hiddenCount={focusHiddenAbove}
                  isLoading={false}
                  onClick={onExitFocus}
                />
              )}

              {/* Sözlük-convention bar: older entries are collapsed, clicking reveals the previous chunk */}
              {collapseActive && onLoadOlderEntries && (
                <CollapsedEntriesBar
                  hiddenCount={collapseHiddenCount}
                  isLoading={isLoadingOlderEntries}
                  onClick={onLoadOlderEntries}
                />
              )}

              {/* Admin-pinned entry of this match — rendered above the regular list */}
              <PinnedEntryBlock type="MATCH" targetId={selectedMatch?.id} />

              <EntryList entries={entries} onDelete={onDeleteEntry} />

              {focusActive && onExitFocus && (
                <CollapsedEntriesBar
                  hiddenCount={focusHiddenBelow}
                  isLoading={false}
                  onClick={onExitFocus}
                />
              )}

              {/*
               * "X entry daha" load-more button — appends next page below current entries.
               * Shown only while more pages remain. Pagination is always rendered below it
               * so the user can also jump to a specific page directly.
               */}
              {hasMoreEntries && (
                <button
                  className={`main-content__load-more ${isLoadingMoreEntries ? "loading" : ""}`}
                  onClick={loadMoreEntries}
                  disabled={isLoadingMoreEntries}
                >
                  {isLoadingMoreEntries
                    ? t("load_more_loading", "yukleniyor...")
                    : t("load_more_entries", "{{count}} entry daha", { count: remainingEntries })}
                </button>
              )}

              {/* Bottom pagination — always visible so the user can jump to any page
                  (hidden in collapsed mode, where the whole topic is not paginated) */}
              {!collapseActive && !focusActive && (
                <div className="pagination-bottom">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    paginate={paginate}
                  />
                </div>
              )}
            </>
          )}

          {/* EntryPostForm is always the last element — below entries, load-more, and pagination */}
          {isAuthenticated ? (
            <EntryPostForm
              selectedMatch={selectedMatch}
              onPostEntry={onPostEntry}
              isLocked={selectedMatch?.isLocked ?? false}
              lockReason={selectedMatch?.lockReason}
            />
          ) : (
            <p className="login-prompt-message">{t("login_to_post_entry")}</p>
          )}
        </>
      ) : (
        // Message displayed when no match is selected
        <p className="no-match-selected-message">{t("no_match_selected_message")}</p>
      )}
    </main>
  );
};